/**
 * Recent files tracker.
 *
 * Two backends:
 *   - Desktop (Electron): mirrors the existing electron-store list via
 *     `window.electronAPI.getRecentFiles()` — main process is the
 *     authoritative source (File > Open Recent menu uses the same list).
 *   - Web: stores {name, openedAt} in localStorage. We never hold the actual
 *     file bytes — only metadata — so re-opening from the palette navigates
 *     to /viewer and prompts the user to pick the file again.
 *
 * The web tracker is best-effort: it survives a refresh but not a cleared
 * browser cache, and it's per-device (no cloud sync). For richer recents,
 * use the desktop app or Cloud documents.
 */
const STORAGE_KEY = 'scrivar_recent_files';
const MAX_RECENT = 10;

function isDesktop() {
  return typeof window !== 'undefined' && !!window.electronAPI;
}

function readWeb() {
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeWeb(list) {
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
  } catch {
    // localStorage may be unavailable (private mode, quota exceeded). Silent
    // failure — the next read just gets [] and the palette hides the section.
  }
}

/**
 * Record a file as recently opened.
 * @param {object} entry — { name, size?, openedAt? }
 *   name is required. size is optional context. openedAt defaults to now.
 *
 * Desktop is a no-op — the main process records its own recent list when
 * files are opened via dialogs / file associations / drag-drop. Calling this
 * from the renderer would create a parallel list that drifts from the
 * native File > Open Recent menu.
 */
export function recordRecent({ name, size, openedAt }) {
  if (!name) return;
  if (isDesktop()) return;
  const list = readWeb();
  // De-duplicate by name + size (web has no path), then prepend.
  const filtered = list.filter((e) => !(e.name === name && e.size === size));
  filtered.unshift({ name, size, openedAt: openedAt ?? Date.now() });
  writeWeb(filtered);
}

/**
 * Return the recent list (newest first), max 10 entries.
 * Resolves with [] if the platform has no recents or localStorage is blocked.
 */
export async function getRecent() {
  if (isDesktop()) {
    try {
      const list = await window.electronAPI.getRecentFiles();
      return Array.isArray(list) ? list.slice(0, MAX_RECENT) : [];
    } catch {
      return [];
    }
  }
  return readWeb();
}

/**
 * Wipe the web recents list. Desktop has its own "Clear Recent" menu in File.
 */
export function clearRecent() {
  if (isDesktop()) return;
  try {
    window.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
